import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, FileText, Handshake, LifeBuoy, ListChecks, Quote, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { JobCard } from "@/components/jobs/JobCard";
import { QuickApplyDialog } from "@/components/apply/QuickApplyDialog";
import { ShareButton } from "@/components/common/ShareButton";

const WHY_ITEMS = [
  { icon: Send, title: "Simple Application", desc: "Apply to any job in seconds with just your name and mobile number. No lengthy forms, no resume required." },
  { icon: Handshake, title: "Trusted Recruitment Process", desc: "Every application is handled by a real recruiter who guides you from application to offer." },
  { icon: ListChecks, title: "Application Tracking", desc: "Know exactly where your application stands — from received to contacted to interview to selection." },
  { icon: FileText, title: "Free Resume Builder", desc: "Build a professional resume with live preview and PDF download. Completely free, forever." },
  { icon: LifeBuoy, title: "Recruiter Support", desc: "Your assigned recruiter is one WhatsApp message away whenever you need help." },
];

const STORIES = [
  { name: "Ramesh", role: "Placed as Relationship Officer", quote: "I applied from my phone during lunch break. The recruiter called me the same week and guided me through the interview. The tracking page kept me updated at every step." },
  { name: "Ayesha", role: "Placed as Customer Support Executive", quote: "As a fresher I had no resume. I applied first, built my resume with the free builder later, and got selected. The whole process felt transparent and respectful." },
  { name: "Vikram", role: "Placed as Staff Nurse", quote: "What I liked most was the honesty — I could see my application status change in real time, and my recruiter was always reachable on WhatsApp." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [applyJob, setApplyJob] = useState(null);

  useEffect(() => {
    api.get("/jobs").then((res) => setJobs(res.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div>
      {/* 1. Hero */}
      <section className="hero-gradient">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 text-center">
          <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs sm:text-sm font-semibold px-4 py-1.5" data-testid="hero-free-badge">
            100% FREE for Job Seekers
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight font-heading leading-[1.08] max-w-3xl mx-auto">
            A Better Hiring Experience <span className="text-primary">Starts Here.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-xl mx-auto">
            Explore verified opportunities, apply in seconds, and track your application from one place.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" className="rounded-lg" onClick={() => navigate("/jobs")} data-testid="hero-find-jobs-button">
              Find Jobs <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="rounded-lg bg-white" onClick={() => navigate("/resume-builder")} data-testid="hero-build-resume-button">
              Build Resume for Free
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs sm:text-sm text-muted-foreground" data-testid="hero-free-points">
            {["Free Resume Builder", "Free Job Applications", "No Registration Fee", "No Placement Charges", "No Hidden Charges"].map((f) => (
              <span key={f} className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {f}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Why Job Seekers Choose Udhyog Tech */}
      <section className="py-14 sm:py-16 bg-white border-y border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Why Job Seekers Choose Udhyog Tech</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10" data-testid="why-us-section">
            {WHY_ITEMS.map((item, i) => (
              <Card key={i} className="rounded-xl border-border shadow-none hover:shadow-[var(--shadow-sm)] transition-shadow">
                <CardContent className="p-6">
                  <span className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <item.icon className="h-5 w-5" />
                  </span>
                  <p className="font-heading font-semibold mt-4">{item.title}</p>
                  <p className="text-sm text-muted-foreground mt-2">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Latest Jobs */}
      <section className="py-14 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Latest Jobs</h2>
              <p className="text-muted-foreground mt-1 text-sm">Fresh opportunities from trusted hiring brands.</p>
            </div>
            <Button variant="outline" className="rounded-lg bg-white hidden sm:flex" onClick={() => navigate("/jobs")} data-testid="landing-view-all-jobs">
              View all <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="landing-latest-jobs">
            {jobs.map((job) => (
              <JobCard key={job.id} job={job} onInterested={() => setApplyJob(job)} />
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Button variant="outline" className="rounded-lg bg-white" onClick={() => navigate("/jobs")}>View all jobs</Button>
          </div>
        </div>
      </section>

      {/* 4. Free Resume Builder */}
      <section className="py-14 sm:py-16 bg-white border-y border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium px-3 py-1">
              100% Free · No hidden charges
            </span>
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mt-4">Build a Professional Resume for Free</h2>
            <p className="text-muted-foreground mt-3">
              Whether you are a fresher or an experienced professional, create a polished resume in minutes — with live preview, multiple templates and instant PDF download.
            </p>
            <ul className="mt-5 space-y-2.5">
              {["Multiple professional templates", "Live preview while you type", "Freshers and experienced professionals supported", "Smart resume — empty sections hide automatically", "Download as PDF instantly"].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <Button size="lg" className="rounded-lg" onClick={() => navigate("/resume-builder")} data-testid="resume-section-cta">
                Build Resume for Free <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <ShareButton
                url={`${window.location.origin}/resume-builder`}
                text="Create your FREE professional resume with Udhyog Tech:"
                testIdPrefix="landing-resume-share"
                size="lg"
              />
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="rounded-2xl border border-border bg-muted/40 p-6">
              <div className="bg-white rounded-xl shadow-[var(--shadow-md)] p-6 max-w-sm mx-auto">
                <div className="h-3 w-32 bg-slate-800 rounded" />
                <div className="h-2 w-44 bg-slate-200 rounded mt-2" />
                <div className="h-px bg-slate-200 my-4" />
                <div className="h-2 w-20 bg-primary/60 rounded" />
                <div className="space-y-1.5 mt-2">
                  <div className="h-2 w-full bg-slate-100 rounded" />
                  <div className="h-2 w-5/6 bg-slate-100 rounded" />
                </div>
                <div className="h-2 w-20 bg-primary/60 rounded mt-4" />
                <div className="space-y-1.5 mt-2">
                  <div className="h-2 w-full bg-slate-100 rounded" />
                  <div className="h-2 w-4/6 bg-slate-100 rounded" />
                </div>
                <div className="flex gap-1.5 mt-4">
                  {[1, 2, 3].map((i) => <div key={i} className="h-4 w-14 bg-blue-50 border border-blue-200 rounded-full" />)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Success Stories */}
      <section className="py-14 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Success Stories</h2>
            <p className="text-muted-foreground mt-2 text-sm">Real journeys from application to placement.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-5" data-testid="success-stories-section">
            {STORIES.map((s, i) => (
              <Card key={i} className="rounded-xl border-border shadow-none">
                <CardContent className="p-6">
                  <Quote className="h-6 w-6 text-primary/30" />
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">“{s.quote}”</p>
                  <div className="flex items-center gap-3 mt-5">
                    <span className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                      {s.name.charAt(0)}
                    </span>
                    <div>
                      <p className="text-sm font-medium">{s.name}</p>
                      <p className="text-xs text-emerald-700">{s.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <QuickApplyDialog job={applyJob} open={!!applyJob} onOpenChange={(v) => !v && setApplyJob(null)} />
    </div>
  );
}
